/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IComponentProps, ICoreContext } from '@msdyn365-commerce/core';
import { isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

/**
 * Resources which are used in order details header.
 */
export interface IOrderDetailsTableHeaderResources {
    orderDetailsProductNumberText: string;
    orderDetailsProductText: string;
    orderDetailsUnitPriceText: string;
    orderDetailsModeOfDeliveryText: string;
    orderDetailsStatusText: string;
    orderDetailsQuantityText: string;
    orderDetailsUnitOfMeasureText: string;
    orderDetailsTotalText: string;
    orderDetailsActionsText: string;
}

/**
 * Configuration of the component.
 */
export interface IOrderDetailsTableHeaderProps extends IComponentProps {
    context: ICoreContext;
    className: string;

    resources: IOrderDetailsTableHeaderResources;
}

/**
 * Renders desktop view cells.
 * @param props - Configuration of the module.
 * @returns List of cells for the header.
 */
const getDesktopHeaderCells = (props: IOrderDetailsTableHeaderProps) => {
    const { resources } = props;

    const columnWithNumberClassName = `${props.className}__row__column-with-number`;

    return (
        <>
            <th />
            <th>
                {resources.orderDetailsProductNumberText}
            </th>
            <th>
                {resources.orderDetailsProductText}
            </th>
            <th className={columnWithNumberClassName}>
                {resources.orderDetailsUnitPriceText}
            </th>
            <th>
                {resources.orderDetailsModeOfDeliveryText}
            </th>
            <th>
                {resources.orderDetailsStatusText}
            </th>
            <th className={columnWithNumberClassName}>
                {resources.orderDetailsQuantityText}
            </th>
            <th>
                {resources.orderDetailsUnitOfMeasureText}
            </th>
            <th className={columnWithNumberClassName}>
                {resources.orderDetailsTotalText}
            </th>
        </>
    );
};

/**
 * Renders mobile view cells.
 * @param props - Configuration of the module.
 * @returns List of cells for the header.
 */
const getMobileHeaderCells = (props: IOrderDetailsTableHeaderProps) => {
    const { resources } = props;
    return (
        <>
            <th />
            <th className={`${props.className}__row`}>
                {resources.orderDetailsProductText}
            </th>
            <th />
        </>
    );
};

/**
 * Order details table header component.
 * @param props - Configuration of the component.
 * @returns React node with the header.
 */
export const OrderDetailsTableHeaderComponent: React.FC<IOrderDetailsTableHeaderProps> = (props: IOrderDetailsTableHeaderProps) => {
    const rowClassName = `${props.className}__row`;

    let headerCells: JSX.Element;
    const isMobileView = isMobile({ variant: VariantType.Viewport, context: props.context.request }) === 'xs';
    if (isMobileView) {
        headerCells = getMobileHeaderCells(props);
    } else {
        headerCells = getDesktopHeaderCells(props);
    }

    return (
        <thead className={props.className}>
            <tr className={rowClassName}>
                {headerCells}
            </tr>
        </thead>
    );
};
